"use client";

import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Lock, ShieldCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ThemeToggle } from "@/components/ui/theme-toggle";
import { getMyEffectivePermissions } from "@/lib/api/endpoints/permissions";
import { setRouteFlash } from "@/lib/navigation/route-flash";
import { resolveAccessibleRoute } from "@/lib/navigation/sidebar-nav";
import { useAuth } from "@/modules/auth/hooks";
import { useAuthStore } from "@/modules/auth/store";
import { useThemeStore } from "@/store/theme-store";
import { DataloggerLoginScene } from "@/src/login/datalogger-login-scene";

export type LoginFormProps = {
  nextPath: string;
  authErrorFromRedirect?: boolean;
};

export function LoginForm({ nextPath, authErrorFromRedirect }: LoginFormProps) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useThemeStore((s) => s.theme);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(email, password);
      const currentUser = useAuthStore.getState().user;
      let destination = nextPath;

      if (currentUser) {
        try {
          const permissions = await getMyEffectivePermissions();
          const permissionMap = permissions.modules.reduce<Record<string, (typeof permissions.modules)[number]>>((acc, module) => {
            acc[module.module_key] = module;
            return acc;
          }, {});
          destination = resolveAccessibleRoute(currentUser, permissionMap, nextPath);
          if (destination !== nextPath) {
            setRouteFlash("You don't have permission to access that page. Redirected to an available module.");
          }
        } catch {
          destination = resolveAccessibleRoute(currentUser, undefined, nextPath);
        }
      }

      navigate(destination, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <main
      className="grid min-h-screen bg-background text-foreground lg:grid-cols-[minmax(0,1fr)_min(480px,38%)] xl:grid-cols-[minmax(0,1fr)_520px]"
      style={{ minHeight: "100dvh" }}
    >
      {/* Hero — left column on desktop, top band on mobile */}
      <section className="relative order-1 min-h-[280px] overflow-hidden sm:min-h-[320px] lg:order-none lg:min-h-0 lg:h-full">
        <div className="h-full min-h-[280px] sm:min-h-[320px] lg:hidden">
          <DataloggerLoginScene mode={theme} compact />
        </div>
        <div className="hidden h-full min-h-0 lg:block">
          <DataloggerLoginScene mode={theme} />
        </div>
      </section>

      {/* Auth panel — isolated column, no scene bleed-through */}
      <section className="relative order-2 flex flex-col border-t border-border bg-card shadow-card lg:order-none lg:border-l lg:border-t-0">
        <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
          <ThemeToggle variant="icon" />
        </div>

        <motion.div
          className="flex flex-1 flex-col justify-center px-6 py-10 sm:px-10 lg:px-12 lg:py-14"
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-8 lg:hidden">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-accent">Atech Telemetry</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">Datalogger command center</h1>
          </div>

          <motion.div
            className="mb-8 hidden lg:block"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.55 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/25 bg-accent/10 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-accent">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Secure uplink
            </div>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight text-foreground">Access console</h1>
            <p className="mt-2 text-sm leading-relaxed text-muted">
              Authenticate to monitor live logger channels, alerts, and fleet telemetry.
            </p>
          </motion.div>

          <motion.div
            className="relative overflow-hidden rounded-2xl border border-border bg-background p-6 shadow-card sm:p-8"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/10 blur-3xl" aria-hidden />
            <div className="pointer-events-none absolute -bottom-16 -left-10 h-28 w-28 rounded-full bg-accent/10 blur-3xl" aria-hidden />

            <div className="relative flex items-center gap-3 border-b border-border pb-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-accent/25 bg-accent/10 text-accent">
                <ShieldCheck className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted">Operator gate</p>
                <p className="text-sm font-medium text-foreground">Encrypted session · HS256</p>
              </div>
            </div>

            <form method="post" encType="application/x-www-form-urlencoded" noValidate onSubmit={handleSubmit} className="relative mt-6 space-y-5">
              <input type="hidden" name="next" value={nextPath} />

              <label className="block space-y-2">
                <span className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted">Operator ID</span>
                <input
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  required
                  placeholder="you@workspace.dev"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ui-control login-field h-12 w-full"
                />
              </label>

              <label className="block space-y-2">
                <span className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-muted">Passphrase</span>
                <div className="relative">
                  <input
                    name="password"
                    type={isPasswordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="ui-control login-field h-12 w-full pr-11"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setIsPasswordVisible((visible) => !visible)}
                    className="absolute inset-y-0 right-0 inline-flex w-11 items-center justify-center rounded-r-[inherit] text-muted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    aria-label={isPasswordVisible ? "Hide passphrase" : "Show passphrase"}
                    title={isPasswordVisible ? "Hide passphrase" : "Show passphrase"}
                  >
                    {isPasswordVisible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                  </button>
                </div>
              </label>

              {authErrorFromRedirect && !error ? (
                <p className="rounded-lg border border-error/30 bg-error/10 px-3 py-2.5 text-sm text-error">
                  Sign-in failed. Check your credentials and try again.
                </p>
              ) : null}
              {error ? (
                <p className="rounded-lg border border-error/30 bg-error/10 px-3 py-2.5 text-sm text-error">{error}</p>
              ) : null}

              <div className="flex items-center justify-between gap-3 pt-1">
                <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                  <Lock className="h-3.5 w-3.5" aria-hidden />
                  TLS-ready channel
                </span>
                <Link to="/forgot-password" className="text-xs text-link transition-colors hover:text-link-hover">
                  Forgot password?
                </Link>
              </div>

              <motion.button
                type="submit"
                disabled={pending}
                className="btn btn-primary group relative h-12 w-full overflow-hidden !rounded-xl"
                whileHover={pending ? undefined : { scale: 1.01 }}
                whileTap={pending ? undefined : { scale: 0.99 }}
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {pending ? "Establishing uplink…" : "Enter telemetry console"}
                  {!pending ? <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden /> : null}
                </span>
                {!pending ? (
                  <motion.span
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    animate={{ x: ["-120%", "120%"] }}
                    transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.2 }}
                    aria-hidden
                  />
                ) : null}
              </motion.button>
            </form>
          </motion.div>

          <p className="mt-6 text-center text-[0.68rem] leading-relaxed text-muted lg:text-left">
            Multi-tenant IoT platform · MQTT · WebSocket · Edge ingest
          </p>
        </motion.div>
      </section>
    </main>
  );
}
